import React from "react";
import {
  Box,
  Typography,
  Container,
  Grid,
  Button,
  Alert,
} from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SpeedIcon from "@mui/icons-material/Speed";
import RefreshIcon from "@mui/icons-material/Refresh";
import DevicesIcon from "@mui/icons-material/Devices";
import EditNoteIcon from "@mui/icons-material/EditNote";

import { useAnalyticsData } from "./hooks/useAnalyticsData";
import { KPICard } from "./components/KPICard";
import { UsageChart } from "./components/UsageChart";
import { PipelineChart } from "./components/PipelineChart";
import { ReportActivityChart } from "./components/ReportActivityChart";
import { StationBreakdownChart } from "./components/StationBreakdownChart";
import { AnalyticsFilters } from "./components/AnalyticsFilters";
import { PerformancePanel } from "./components/PerformancePanel";
import { ErrorPanel } from "./components/ErrorPanel";
import { AnalyticsSkeleton } from "./components/AnalyticsSkeleton";

function formatGenTime(seconds: number): string {
  if (!seconds || !Number.isFinite(seconds)) return "0s";
  if (seconds < 60) return `${seconds.toFixed(0)}s`;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

export const AnalyticsPage: React.FC = () => {
  const {
    filters,
    handleTimeRangeChange,
    handleGroupByChange,
    handleStationChange,
    handleCustomDateChange,
    usageOverTime,
    reportActivity,
    pipeline,
    stationBreakdown,
    kpiData,
    performance,
    errors,
    maxUsage,
    exportToCSV,
    allStations,
    loading,
    error,
    refetch,
  } = useAnalyticsData();

  return (
    <Container sx={{ overflow: "auto" }}>
      <Box
        sx={{
          p: 3,
          pb: 4,
          bgcolor: (t) => t.palette.background.default,
          minHeight: "100vh",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: { xs: "flex-start", md: "flex-end" },
            justifyContent: "space-between",
            gap: 2,
            mb: 3,
          }}
        >
          <Box>
            <Typography variant="h5" fontWeight={600}>
              Report Analytics
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Report generation pipeline, engagement, and station breakdown.
            </Typography>
          </Box>

          <AnalyticsFilters
            filters={filters}
            onTimeRangeChange={handleTimeRangeChange}
            onGroupByChange={handleGroupByChange}
            onStationChange={handleStationChange}
            onCustomDateChange={handleCustomDateChange}
            onExport={exportToCSV}
            stations={allStations}
          />
        </Box>

        {/* Error state */}
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 3 }}
            action={
              <Button
                color="inherit"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={refetch}
              >
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        {/* Loading state */}
        {loading && <AnalyticsSkeleton />}

        {/* Data content */}
        {!loading && !error && (
          <>
            {/* KPI cards */}
            <Grid container spacing={2} sx={{ mb: 3, alignItems: "stretch" }}>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <KPICard
                  title="Reports Generated"
                  description="Total number of reports that completed generation successfully in the selected time period."
                  value={kpiData.totalReports}
                  icon={<DescriptionIcon />}
                  color="primary"
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <KPICard
                  title="Success Rate"
                  description="Percentage of report generation jobs that completed successfully vs. failed. Target is 95%."
                  value={kpiData.successRate}
                  unit="%"
                  icon={<CheckCircleIcon />}
                  color="success"
                  progress={{ value: kpiData.successRate, target: "Target: 95%" }}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <KPICard
                  title="Avg Generation Time"
                  description="Median time from job creation to report completion (p50). Capped at 30 min to exclude retries and stuck jobs."
                  value={formatGenTime(kpiData.avgGenerationTimeSec)}
                  icon={<SpeedIcon />}
                  color="info"
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <KPICard
                  title="Total Hours Saved"
                  description="Estimated time savings based on 4 hours saved per AI-generated report compared to manual report writing."
                  value={(kpiData.totalHoursSaved ?? 0).toFixed(1)}
                  unit="hrs"
                  icon={<AccessTimeIcon />}
                  color="warning"
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <KPICard
                  title="Sessions"
                  description="Number of WebSocket editing sessions opened. Each time a user opens a report for editing counts as one session."
                  value={kpiData.totalSessions}
                  icon={<DevicesIcon />}
                  color="info"
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <KPICard
                  title="Report Edits"
                  description="Total number of individual save operations made while editing reports in the WebSocket editor."
                  value={kpiData.totalEdits}
                  icon={<EditNoteIcon />}
                  color="warning"
                />
              </Grid>
            </Grid>

            {/* Charts row 1: Usage + Pipeline */}
            <Grid container spacing={2} sx={{ mb: 2, alignItems: "stretch" }}>
              <Grid size={{ xs: 12, md: 8 }}>
                <UsageChart data={usageOverTime} maxValue={maxUsage} />
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <PipelineChart data={pipeline} />
              </Grid>
            </Grid>

            {/* Charts row 2: Activity + Performance + Station */}
            <Grid container spacing={2} sx={{ mb: 2, alignItems: "stretch" }}>
              <Grid size={{ xs: 12, md: 4 }}>
                <ReportActivityChart data={reportActivity} />
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <PerformancePanel data={performance} />
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <StationBreakdownChart data={stationBreakdown} />
              </Grid>
            </Grid>

            {/* Charts row 3: Errors */}
            <Grid container spacing={2} sx={{ mb: 2, alignItems: "stretch" }}>
              <Grid size={{ xs: 12, md: 4 }}>
                <ErrorPanel data={errors} />
              </Grid>
            </Grid>
          </>
        )}
      </Box>
    </Container>
  );
};
